import { AxiosResponse } from 'axios';
import { api } from './config';
import {
  ShippingPrice, CreateShippingPrice, UpdateShippingPrice, ShippingPriceHistory,
  ShippingPriceByCapacity, CreateShippingPriceByCapacity, UpdateShippingPriceByCapacity,
  ShippingPriceMatrix, BulkUpdateShippingPriceByCapacity, ShippingPriceByCapacityHistory,
  TollFee, CreateTollFee, TollBoothLocation, LoadUnloadFee, CreateLoadUnloadFee,
  RM2Fee, CreateRM2Fee, UpdateRM2Fee, RM2FeeHistory, FeeStructure,
  CreateFeeStructure, UpdateFeeStructure, Province, District
} from '../../types';

export const shippingPriceApi = {
  // Location APIs
  getProvinces: (): Promise<AxiosResponse<Province[]>> =>
    api.get('/shippingprice/provinces'),
    
  getDistricts: (provinceId?: number): Promise<AxiosResponse<District[]>> =>
    api.get(`/shippingprice/districts${provinceId ? `?provinceId=${provinceId}` : ''}`),
    
  // Price Management APIs
  getAll: (): Promise<AxiosResponse<ShippingPrice[]>> =>
    api.get('/shippingprice'),
    
  getById: (id: number): Promise<AxiosResponse<ShippingPrice>> =>
    api.get(`/shippingprice/${id}`),
    
  create: (data: CreateShippingPrice): Promise<AxiosResponse<ShippingPrice>> =>
    api.post('/shippingprice', data),
    
  update: (id: number, data: UpdateShippingPrice): Promise<AxiosResponse<ShippingPrice>> =>
    api.put(`/shippingprice/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/shippingprice/${id}`),
    
  // History API
  getHistory: (): Promise<AxiosResponse<ShippingPriceHistory[]>> =>
    api.get('/shippingprice/history'),
    
  // Price calculation API
  getPriceForDistrict: (districtId: number): Promise<AxiosResponse<number>> =>
    api.get(`/shippingprice/calculate/${districtId}`)
};

// Shipping Price by Capacity API (Matrix-based pricing)
export const shippingPriceByCapacityApi = {
  // Get price matrix - locations (rows) x capacities (columns)
  getMatrix: (provinceId?: number, locationType?: string): Promise<AxiosResponse<ShippingPriceMatrix>> =>
    api.get('/shippingpricebycapacity/matrix', { 
      params: { provinceId, locationType } 
    }),
    
  // Get all prices (flat list)
  getAll: (): Promise<AxiosResponse<ShippingPriceByCapacity[]>> =>
    api.get('/shippingpricebycapacity'),
    
  // Get single price
  getById: (id: number): Promise<AxiosResponse<ShippingPriceByCapacity>> =>
    api.get(`/shippingpricebycapacity/${id}`),
    
  // Create a new price
  create: (data: CreateShippingPriceByCapacity): Promise<AxiosResponse<ShippingPriceByCapacity>> =>
    api.post('/shippingpricebycapacity', data),
    
  // Update a price
  update: (id: number, data: UpdateShippingPriceByCapacity): Promise<AxiosResponse<ShippingPriceByCapacity>> =>
    api.put(`/shippingpricebycapacity/${id}`, data),
    
  // Bulk update prices (for matrix editing)
  bulkUpdate: (data: BulkUpdateShippingPriceByCapacity): Promise<AxiosResponse<{ message: string; results: any[] }>> =>
    api.post('/shippingpricebycapacity/bulk-update', data),
    
  // Delete a price
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/shippingpricebycapacity/${id}`),
    
  // Get history
  getHistory: (provinceId?: number, districtId?: number, capacityTonsId?: number): 
    Promise<AxiosResponse<ShippingPriceByCapacityHistory[]>> =>
 api.get('/shippingpricebycapacity/history', { 
      params: { provinceId, districtId, capacityTonsId } 
    }),
    
  // Calculate price for location + capacity
  calculatePrice: (params: { districtId?: number; provinceId?: number; capacityTonsId: number; shippingDate?: string }): 
    Promise<AxiosResponse<number>> =>
    api.get('/shippingpricebycapacity/calculate', { params })
};

// Vehicle API
export const tollFeeApi = {
  getAll: (): Promise<AxiosResponse<TollFee[]>> =>
    api.get('/tollfee'),
    
  getById: (id: number): Promise<AxiosResponse<TollFee>> =>
    api.get(`/tollfee/${id}`),
    
  create: (data: CreateTollFee): Promise<AxiosResponse<TollFee>> =>
    api.post('/tollfee', data),

  update: (id: number, data: any): Promise<AxiosResponse<TollFee>> =>
    api.put(`/tollfee/${id}`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/tollfee/${id}`),
    
  getHistory: (id: number): Promise<AxiosResponse<any[]>> =>
    api.get(`/tollfee/${id}/history`),
  
  seedTollBooths: (): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/tollfee/seed-toll-booths'),
    
  // Get all unique toll booth locations with GPS coordinates
  getTollBoothLocations: (): Promise<AxiosResponse<TollBoothLocation[]>> =>
    api.get('/tollfee/toll-booth-locations')
};

// Load/Unload Fee API
export const loadUnloadFeeApi = {
  getAll: (): Promise<AxiosResponse<LoadUnloadFee[]>> =>
    api.get('/loadunloadfee'),
    
  getById: (id: number): Promise<AxiosResponse<LoadUnloadFee>> =>
    api.get(`/loadunloadfee/${id}`),
    
  getByCapacity: (capacityTonsId: number): Promise<AxiosResponse<LoadUnloadFee>> =>
    api.get(`/loadunloadfee/by-capacity/${capacityTonsId}`),
    
  create: (data: CreateLoadUnloadFee): Promise<AxiosResponse<LoadUnloadFee>> =>
    api.post('/loadunloadfee', data),
    
  update: (id: number, data: any): Promise<AxiosResponse<LoadUnloadFee>> =>
    api.put(`/loadunloadfee/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/loadunloadfee/${id}`),
    
  getHistory: (id: number): Promise<AxiosResponse<any[]>> =>
    api.get(`/loadunloadfee/${id}/history`)
};

// RM2Fee API
export const rm2FeeApi = {
  getAll: (): Promise<AxiosResponse<RM2Fee[]>> =>
    api.get('/rm2fee'),
    
  getById: (id: number): Promise<AxiosResponse<RM2Fee>> =>
    api.get(`/rm2fee/${id}`),
    
  create: (data: CreateRM2Fee): Promise<AxiosResponse<RM2Fee>> =>
    api.post('/rm2fee', data),
    
  update: (id: number, data: UpdateRM2Fee): Promise<AxiosResponse<RM2Fee>> =>
    api.put(`/rm2fee/${id}`, data),
    
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rm2fee/${id}`),
    
  getHistory: (id: number): Promise<AxiosResponse<RM2FeeHistory[]>> =>
    api.get(`/rm2fee/${id}/history`),
    
  calculateRM2Fee: (districtId?: number, capacityTonsId?: number): Promise<AxiosResponse<{ fee: number, applicable: boolean }>> =>
    api.get('/rm2fee/calculate', { params: { districtId, capacityTonsId } })
};

// Customer API
export const feeStructureApi = {
  getAll: (): Promise<AxiosResponse<FeeStructure[]>> =>
    api.get('/feestructure'),
  getById: (id: number): Promise<AxiosResponse<FeeStructure>> =>
    api.get(`/feestructure/${id}`),
  getActive: (feeType: string, asOfDate?: string): Promise<AxiosResponse<FeeStructure>> =>
    api.get(`/feestructure/active/${feeType}`, { params: { asOfDate } }),
  create: (data: CreateFeeStructure): Promise<AxiosResponse<FeeStructure>> =>
    api.post('/feestructure', data),
  update: (id: number, data: UpdateFeeStructure): Promise<AxiosResponse<void>> =>
    api.put(`/feestructure/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/feestructure/${id}`)
};

// Prepaid API
